/**
 * Surface modes — ADR-038 §14 (J4, the surface jiggle): **the natural
 * vibration shapes of a patch of surface, solved from its own geometry.**
 *
 * ONE responsibility: given a triangle patch (positions, faces) and the
 * vertices held still (a soft mass's ring — none for a closed surface),
 * return its lowest membrane modes: per vertex the scalar φk, its eigenvalue
 * λk, and the surface gradient ∇φk the normals need. A mode moves the surface
 * along its own normal, u = q·φ·n, so to first order the normal turns by
 * −q·∇φ (the pudding's baked (∇Φ)ᵀn, §14.1).
 *
 * THE PROBLEM: L φ = λ M φ, with L the cotangent Laplacian (positive
 * semidefinite) and M the lumped (barycentric) mass — the discrete
 * Laplace–Beltrami eigenproblem. Held vertices are removed (Dirichlet φ = 0).
 * On a flat unit disc held at the rim this is the drumhead (λ = j²ₘₙ: 5.78,
 * 14.68 ×2, …); on a unit sphere with nothing held, the spherical harmonics
 * (λ = l(l+1): 0, 2 ×3, 6 ×5, …). Both are the tests.
 *
 * THE METHOD — shifted block inverse iteration with Rayleigh–Ritz:
 *   Y = (L + σM)⁻¹ M X   ((L + σM) factored ONCE — profile Cholesky under a
 *                         Reverse Cuthill–McKee ordering — so each step is two
 *                         triangular sweeps; σ > 0 keeps a closed surface's
 *                         singular L solvable)
 *   then the small projected problem (YᵀLY) c = λ (YᵀMY) c is solved exactly
 *   (Cholesky + Jacobi), X ← Y·C, and the loop stops when the kept Ritz
 *   values stop moving. The block carries a few more vectors than asked for,
 *   so a degenerate pair (every round patch has them) converges as a pair.
 *   Deterministic: a seeded start, no Math.random.
 *
 * ENGINE-FREE ON PURPOSE (§14.3, the pudding pistol): arrays in, arrays out,
 * so World can run the same solve at runtime.
 *
 * Depends on: ir/errors. Depended on by: the region jiggle (app), tests.
 */
import { KernelError } from '../ir/errors.js';

export interface PatchModesInput {
  /** xyz per vertex, bind space. */
  readonly positions: ArrayLike<number>;
  /** Three vertex indices per triangle. */
  readonly indices: ArrayLike<number>;
  /** 1 = held still (φ = 0): the ring where a mass meets the body. Omit for a closed surface. */
  readonly pinned?: ArrayLike<number>;
  /** How many of the lowest modes to return. */
  readonly count: number;
}

export interface SurfaceMode {
  /** The eigenvalue (1/length²): frequency ∝ √λ. */
  readonly lambda: number;
  /** φ per vertex, M-normalised (Σ Mᵢ φᵢ² = 1); 0 on held vertices. */
  readonly phi: Float64Array;
  /** ∇φ per vertex (xyz), the area-weighted average of its faces' gradients. */
  readonly gradient: Float64Array;
}

export interface PatchModes {
  readonly modes: readonly SurfaceMode[];
  /** Lumped mass per vertex (a third of each adjacent face's area). */
  readonly mass: Float64Array;
  /** Outer iterations used, and whether the Ritz values settled. */
  readonly iterations: number;
  readonly converged: boolean;
}

/** A patch the solver cannot use: said plainly (§14.6, fail loud). */
export class SurfaceModesRefusal extends KernelError {
  constructor(message: string) {
    super(`surface modes refused: ${message}`);
  }
}

const EXTRA = 4;
const MAX_OUTER = 200;
const RITZ_TOL = 1e-7;

export function solvePatchModes(input: PatchModesInput): PatchModes {
  const P = input.positions;
  const I = input.indices;
  const n = Math.floor(P.length / 3);
  if (n === 0 || I.length < 3) throw new SurfaceModesRefusal('the patch has no triangles');
  const { cot, mass } = cotangentLaplacian(P, I, n);

  // The free vertices (not held, and touching at least one face).
  const free: number[] = [];
  const slot = new Int32Array(n).fill(-1);
  for (let v = 0; v < n; v++) {
    if ((input.pinned?.[v] ?? 0) !== 0 || mass[v]! <= 0) continue;
    slot[v] = free.length;
    free.push(v);
  }
  const f = free.length;
  const want = Math.min(input.count, f);
  if (want < 1) throw new SurfaceModesRefusal('no free vertex inside the ring');
  const p = Math.min(f, want + EXTRA);

  // The reduced system in CSR over the free vertices.
  const A = reduce(cot, slot, f);
  const Mf = Float64Array.from(free, (v) => mass[v]!);
  const totalMass = Mf.reduce((s, m) => s + m, 0);
  // σ: a small shift relative to the patch's own scale (1 / area), so a closed
  // surface's constant mode (λ = 0) is solvable and a held patch barely feels it.
  const sigma = 1e-3 / totalMass;
  // (L + σM) never changes across iterations: factor it ONCE (profile Cholesky under a
  // Reverse Cuthill–McKee ordering), so every inverse step is two triangular sweeps.
  const factor = profileCholesky(A, sigma, Mf);

  // Seeded start, M-orthonormalised.
  let X = seeded(f, p);
  mOrthonormalise(X, Mf);
  let ritz: Float64Array = new Float64Array(p).fill(Infinity);
  let C: Float64Array[] = [];
  let iterations = 0;
  let converged = false;
  for (; iterations < MAX_OUTER; iterations++) {
    const Y = X.map((x) => {
      const rhs = new Float64Array(f);
      for (let i = 0; i < f; i++) rhs[i] = Mf[i]! * x[i]!;
      return factor.solve(rhs);
    });
    // One inverse step pulls every column toward the lowest mode; re-orthonormalise (twice, the
    // classical fix for Gram–Schmidt's lost orthogonality) or the projected B goes singular.
    mOrthonormalise(Y, Mf);
    mOrthonormalise(Y, Mf);
    // Rayleigh–Ritz on span(Y).
    const K = gram(Y, (y) => applyL(A, y));
    const B = gram(Y, (y) => y.map((v, i) => v * Mf[i]!));
    const { values, vectors } = generalizedSymmetricEig(K, B);
    C = vectors;
    X = combine(Y, C);
    let moved = 0;
    for (let k = 0; k < want; k++) moved = Math.max(moved, Math.abs(values[k]! - ritz[k]!) / Math.max(Math.abs(values[k]!), 1e-12));
    ritz = values;
    if (moved < RITZ_TOL) {
      converged = true;
      iterations++;
      break;
    }
  }
  if (!converged) throw new SurfaceModesRefusal(`the eigen-solve did not settle in ${MAX_OUTER} iterations`);

  const modes: SurfaceMode[] = [];
  for (let k = 0; k < want; k++) {
    const phi = new Float64Array(n);
    for (let i = 0; i < f; i++) phi[free[i]!] = X[k]![i]!;
    normaliseM(phi, mass);
    modes.push({ lambda: Math.max(0, ritz[k]!), phi, gradient: surfaceGradient(P, I, n, phi) });
  }
  return { modes, mass, iterations, converged };
}

/**
 * Remove a mode's net normal displacement (§14.2.3, flesh is incompressible):
 * subtract the multiple of `base` (the first, no-nodal-line mode) that zeroes
 * Σ Mᵢ φᵢ. Returns the corrected φ and its gradient; `base` itself is not kept.
 */
export function volumeNeutral(mode: SurfaceMode, base: SurfaceMode, mass: ArrayLike<number>): SurfaceMode {
  let net = 0;
  let baseNet = 0;
  for (let i = 0; i < mode.phi.length; i++) {
    net += mass[i]! * mode.phi[i]!;
    baseNet += mass[i]! * base.phi[i]!;
  }
  if (Math.abs(baseNet) < 1e-12) return mode;
  const c = net / baseNet;
  const phi = mode.phi.map((v, i) => v - c * base.phi[i]!);
  const gradient = mode.gradient.map((v, i) => v - c * base.gradient[i]!);
  return { lambda: mode.lambda, phi, gradient };
}

/**
 * A region of a real mesh as the solver wants it (ADR-038 §14): UV seams and
 * hard edges DUPLICATE vertices at one position, and a Laplacian over the
 * duplicates would cut the patch along every seam — the ripple would tear the
 * skin open there. So twins are WELDED by position (quantised to `tolerance`),
 * the faces remapped onto the welded set (degenerate ones dropped), and the
 * RING is the welded patch's topological boundary (an edge used by one face):
 * where the mass meets the rest of the body.
 */
export interface WeldedPatch {
  /** xyz per welded vertex. */
  readonly positions: Float64Array;
  readonly indices: Uint32Array;
  /** 1 on the ring (boundary), 0 inside. */
  readonly pinned: Uint8Array;
  /** For each welded vertex, the original vertex indices it stands for. */
  readonly twins: readonly (readonly number[])[];
}

export function weldPatch(
  positions: ArrayLike<number>,
  vertices: readonly number[],
  faces: readonly number[],
  indices: ArrayLike<number>,
  tolerance = 1e-5,
): WeldedPatch {
  const keyOf = (v: number): string =>
    `${Math.round(positions[v * 3]! / tolerance)},${Math.round(positions[v * 3 + 1]! / tolerance)},${Math.round(positions[v * 3 + 2]! / tolerance)}`;
  const byKey = new Map<string, number>();
  const local = new Map<number, number>();
  const twins: number[][] = [];
  const pos: number[] = [];
  for (const v of vertices) {
    const key = keyOf(v);
    let w = byKey.get(key);
    if (w === undefined) {
      w = twins.length;
      byKey.set(key, w);
      twins.push([]);
      pos.push(positions[v * 3]!, positions[v * 3 + 1]!, positions[v * 3 + 2]!);
    }
    twins[w]!.push(v);
    local.set(v, w);
  }
  const tris: number[] = [];
  const edgeUse = new Map<number, number>();
  const n = twins.length;
  for (const f of faces) {
    const a = local.get(indices[f * 3]!);
    const b = local.get(indices[f * 3 + 1]!);
    const c = local.get(indices[f * 3 + 2]!);
    if (a === undefined || b === undefined || c === undefined || a === b || b === c || a === c) continue;
    tris.push(a, b, c);
    for (const [p, q] of [[a, b], [b, c], [c, a]] as const) {
      const key = p < q ? p * n + q : q * n + p;
      edgeUse.set(key, (edgeUse.get(key) ?? 0) + 1);
    }
  }
  const pinned = new Uint8Array(n);
  for (const [key, uses] of edgeUse) {
    if (uses !== 1) continue;
    pinned[Math.floor(key / n)] = 1;
    pinned[key % n] = 1;
  }
  return { positions: Float64Array.from(pos), indices: Uint32Array.from(tris), pinned, twins };
}

// ───────────────────────── the operators ─────────────────────────

interface Cot {
  /** Off-diagonal weights w_ij = (cot α + cot β) / 2 per undirected edge. */
  readonly edges: Map<number, number>;
}

function edgeKey(a: number, b: number, n: number): number {
  return a < b ? a * n + b : b * n + a;
}

function cotangentLaplacian(P: ArrayLike<number>, I: ArrayLike<number>, n: number): { cot: Cot & { n: number }; mass: Float64Array } {
  const edges = new Map<number, number>();
  const mass = new Float64Array(n);
  for (let t = 0; t + 2 < I.length; t += 3) {
    const v = [I[t]!, I[t + 1]!, I[t + 2]!];
    const area = triangleArea(P, v[0]!, v[1]!, v[2]!);
    if (!(area > 0)) continue;
    for (const k of v) mass[k] = mass[k]! + area / 3;
    for (let c = 0; c < 3; c++) {
      // The angle at v[c] is opposite the edge (v[c+1], v[c+2]).
      const o = v[c]!;
      const a = v[(c + 1) % 3]!;
      const b = v[(c + 2) % 3]!;
      const cot = cotAt(P, o, a, b);
      const key = edgeKey(a, b, n);
      edges.set(key, (edges.get(key) ?? 0) + cot / 2);
    }
  }
  return { cot: { edges, n }, mass };
}

interface Csr {
  readonly rowStart: Int32Array;
  readonly col: Int32Array;
  readonly val: Float64Array;
  /** The diagonal of L (Σ of the row's weights). */
  readonly diag: Float64Array;
}

/** L over the free vertices: L_ii = Σ w_ij over ALL neighbours (held ones too), L_ij = −w_ij between free ones. */
function reduce(cot: Cot & { n: number }, slot: Int32Array, f: number): Csr {
  const rows: Array<Array<[number, number]>> = Array.from({ length: f }, () => []);
  const diag = new Float64Array(f);
  for (const [key, w] of cot.edges) {
    const a = Math.floor(key / cot.n);
    const b = key % cot.n;
    const sa = slot[a]!;
    const sb = slot[b]!;
    if (sa >= 0) diag[sa] = diag[sa]! + w;
    if (sb >= 0) diag[sb] = diag[sb]! + w;
    if (sa >= 0 && sb >= 0) {
      rows[sa]!.push([sb, -w]);
      rows[sb]!.push([sa, -w]);
    }
  }
  const rowStart = new Int32Array(f + 1);
  for (let i = 0; i < f; i++) rowStart[i + 1] = rowStart[i]! + rows[i]!.length;
  const col = new Int32Array(rowStart[f]!);
  const val = new Float64Array(rowStart[f]!);
  for (let i = 0; i < f; i++) {
    let o = rowStart[i]!;
    for (const [j, w] of rows[i]!) {
      col[o] = j;
      val[o++] = w;
    }
  }
  return { rowStart, col, val, diag };
}

function applyL(A: Csr, x: ArrayLike<number>): Float64Array {
  const y = new Float64Array(A.diag.length);
  for (let i = 0; i < y.length; i++) {
    let s = A.diag[i]! * x[i]!;
    for (let o = A.rowStart[i]!; o < A.rowStart[i + 1]!; o++) s += A.val[o]! * x[A.col[o]!]!;
    y[i] = s;
  }
  return y;
}

/**
 * (L + σM) = RRᵀ in profile (envelope) storage under a Reverse Cuthill–McKee
 * ordering: RCM keeps a mesh's envelope near √n wide, so the factor costs
 * ~n·w² once and a solve ~2·n·w. Row i stores columns first[i]..i.
 */
function profileCholesky(A: Csr, sigma: number, M: Float64Array): { solve(rhs: Float64Array): Float64Array } {
  const f = A.diag.length;
  const order = reverseCuthillMcKee(A);
  const rank = new Int32Array(f);
  order.forEach((v, r) => (rank[v] = r));
  const first = new Int32Array(f);
  for (let r = 0; r < f; r++) {
    const v = order[r]!;
    let lo = r;
    for (let o = A.rowStart[v]!; o < A.rowStart[v + 1]!; o++) lo = Math.min(lo, rank[A.col[o]!]!);
    first[r] = lo;
  }
  const rowPtr = new Int32Array(f + 1);
  for (let r = 0; r < f; r++) rowPtr[r + 1] = rowPtr[r]! + (r - first[r]! + 1);
  const L = new Float64Array(rowPtr[f]!);
  const at = (r: number, c: number): number => rowPtr[r]! + (c - first[r]!);
  for (let r = 0; r < f; r++) {
    const v = order[r]!;
    L[at(r, r)] = A.diag[v]! + sigma * M[v]!;
    for (let o = A.rowStart[v]!; o < A.rowStart[v + 1]!; o++) {
      const c = rank[A.col[o]!]!;
      if (c < r) L[at(r, c)] = A.val[o]!;
    }
  }
  for (let r = 0; r < f; r++) {
    for (let c = first[r]!; c <= r; c++) {
      let s = L[at(r, c)]!;
      const k0 = Math.max(first[r]!, first[c]!);
      const br = rowPtr[r]! - first[r]!;
      const bc = rowPtr[c]! - first[c]!;
      for (let k = k0; k < c; k++) s -= L[br + k]! * L[bc + k]!;
      if (c < r) L[at(r, c)] = s / L[at(c, c)]!;
      else {
        if (!(s > 0)) throw new SurfaceModesRefusal('the patch operator is not positive definite (a degenerate patch)');
        L[at(r, r)] = Math.sqrt(s);
      }
    }
  }
  return {
    solve(rhs: Float64Array): Float64Array {
      const y = new Float64Array(f);
      for (let r = 0; r < f; r++) {
        let s = rhs[order[r]!]!;
        const b = rowPtr[r]! - first[r]!;
        for (let k = first[r]!; k < r; k++) s -= L[b + k]! * y[k]!;
        y[r] = s / L[at(r, r)]!;
      }
      for (let r = f - 1; r >= 0; r--) {
        y[r] = y[r]! / L[at(r, r)]!;
        const b = rowPtr[r]! - first[r]!;
        const yr = y[r]!;
        for (let k = first[r]!; k < r; k++) y[k] = y[k]! - L[b + k]! * yr;
      }
      const x = new Float64Array(f);
      for (let r = 0; r < f; r++) x[order[r]!] = y[r]!;
      return x;
    },
  };
}

/** Reverse Cuthill–McKee over the free graph, each component from a low-degree start. */
function reverseCuthillMcKee(A: Csr): Int32Array {
  const f = A.diag.length;
  const degree = (v: number): number => A.rowStart[v + 1]! - A.rowStart[v]!;
  const seen = new Uint8Array(f);
  const out: number[] = [];
  const starts = [...Array(f).keys()].sort((a, b) => degree(a) - degree(b) || a - b);
  for (const s0 of starts) {
    if (seen[s0]) continue;
    seen[s0] = 1;
    const queue = [s0];
    for (let h = 0; h < queue.length; h++) {
      const v = queue[h]!;
      out.push(v);
      const next: number[] = [];
      for (let o = A.rowStart[v]!; o < A.rowStart[v + 1]!; o++) {
        const w = A.col[o]!;
        if (!seen[w]) {
          seen[w] = 1;
          next.push(w);
        }
      }
      next.sort((a, b) => degree(a) - degree(b) || a - b);
      queue.push(...next);
    }
  }
  return Int32Array.from(out.reverse());
}

// ───────────────────────── the small dense problem ─────────────────────────

function gram(Y: Float64Array[], op: (y: Float64Array) => ArrayLike<number>): Float64Array[] {
  const p = Y.length;
  const opY = Y.map(op);
  const G = Array.from({ length: p }, () => new Float64Array(p));
  for (let a = 0; a < p; a++) for (let b = a; b < p; b++) G[a]![b] = G[b]![a] = dot(Y[a]!, opY[b]!);
  // Symmetrise (the operators are symmetric; round-off is not).
  for (let a = 0; a < p; a++) for (let b = a + 1; b < p; b++) G[a]![b] = G[b]![a] = (G[a]![b]! + G[b]![a]!) / 2;
  return G;
}

/** K c = λ B c for small symmetric K and SPD B: Cholesky B = LLᵀ, then Jacobi on L⁻¹ K L⁻ᵀ. Sorted ascending; columns B-orthonormal. */
function generalizedSymmetricEig(K: Float64Array[], B: Float64Array[]): { values: Float64Array; vectors: Float64Array[] } {
  const p = K.length;
  const L = cholesky(B);
  // C = L⁻¹ K L⁻ᵀ
  const Linv = lowerInverse(L);
  const T = matMul(Linv, K);
  const Cm = matMul(T, transpose(Linv));
  const { values, vectors } = jacobiEig(Cm);
  // back-substitute: c = L⁻ᵀ w
  const LinvT = transpose(Linv);
  const order = [...values.keys()].sort((a, b) => values[a]! - values[b]!);
  const outValues = new Float64Array(p);
  const outVectors: Float64Array[] = [];
  order.forEach((k, idx) => {
    outValues[idx] = values[k]!;
    const w = new Float64Array(p);
    for (let i = 0; i < p; i++) w[i] = vectors[i]![k]!;
    const c = new Float64Array(p);
    for (let i = 0; i < p; i++) {
      let s = 0;
      for (let j = 0; j < p; j++) s += LinvT[i]![j]! * w[j]!;
      c[i] = s;
    }
    outVectors.push(c);
  });
  return { values: outValues, vectors: outVectors };
}

function cholesky(B: Float64Array[]): Float64Array[] {
  const p = B.length;
  const L = Array.from({ length: p }, () => new Float64Array(p));
  for (let i = 0; i < p; i++) {
    for (let j = 0; j <= i; j++) {
      let s = B[i]![j]!;
      for (let k = 0; k < j; k++) s -= L[i]![k]! * L[j]![k]!;
      if (i === j) {
        if (!(s > 0)) throw new SurfaceModesRefusal('the search space collapsed (a degenerate patch)');
        L[i]![i] = Math.sqrt(s);
      } else L[i]![j] = s / L[j]![j]!;
    }
  }
  return L;
}

function lowerInverse(L: Float64Array[]): Float64Array[] {
  const p = L.length;
  const X = Array.from({ length: p }, () => new Float64Array(p));
  for (let c = 0; c < p; c++) {
    for (let i = 0; i < p; i++) {
      let s = i === c ? 1 : 0;
      for (let k = 0; k < i; k++) s -= L[i]![k]! * X[k]![c]!;
      X[i]![c] = s / L[i]![i]!;
    }
  }
  return X;
}

/** Cyclic Jacobi for a small symmetric matrix; vectors[i][k] is component i of eigenvector k. */
function jacobiEig(S: Float64Array[]): { values: Float64Array; vectors: Float64Array[] } {
  const p = S.length;
  const A = S.map((r) => Float64Array.from(r));
  const V = Array.from({ length: p }, (_, i) => {
    const r = new Float64Array(p);
    r[i] = 1;
    return r;
  });
  for (let sweep = 0; sweep < 100; sweep++) {
    let off = 0;
    for (let i = 0; i < p; i++) for (let j = i + 1; j < p; j++) off += A[i]![j]! ** 2;
    if (off < 1e-30) break;
    for (let i = 0; i < p; i++) {
      for (let j = i + 1; j < p; j++) {
        const aij = A[i]![j]!;
        if (Math.abs(aij) < 1e-300) continue;
        const theta = (A[j]![j]! - A[i]![i]!) / (2 * aij);
        const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
        const c = 1 / Math.sqrt(t * t + 1);
        const s = t * c;
        for (let k = 0; k < p; k++) {
          const aik = A[i]![k]!;
          const ajk = A[j]![k]!;
          A[i]![k] = c * aik - s * ajk;
          A[j]![k] = s * aik + c * ajk;
        }
        for (let k = 0; k < p; k++) {
          const aki = A[k]![i]!;
          const akj = A[k]![j]!;
          A[k]![i] = c * aki - s * akj;
          A[k]![j] = s * aki + c * akj;
        }
        for (let k = 0; k < p; k++) {
          const vki = V[k]![i]!;
          const vkj = V[k]![j]!;
          V[k]![i] = c * vki - s * vkj;
          V[k]![j] = s * vki + c * vkj;
        }
      }
    }
  }
  return { values: Float64Array.from(A, (r, i) => r[i]!), vectors: V };
}

function matMul(A: Float64Array[], B: Float64Array[]): Float64Array[] {
  const p = A.length;
  const q = B[0]!.length;
  const C = Array.from({ length: p }, () => new Float64Array(q));
  for (let i = 0; i < p; i++)
    for (let k = 0; k < B.length; k++) {
      const a = A[i]![k]!;
      if (a === 0) continue;
      for (let j = 0; j < q; j++) C[i]![j] = C[i]![j]! + a * B[k]![j]!;
    }
  return C;
}

function transpose(A: Float64Array[]): Float64Array[] {
  return Array.from(A[0]!, (_, j) => Float64Array.from(A, (r) => r[j]!));
}

/** X ← Y·C, one column per Ritz vector. */
function combine(Y: Float64Array[], C: Float64Array[]): Float64Array[] {
  const f = Y[0]!.length;
  return C.map((c) => {
    const x = new Float64Array(f);
    for (let a = 0; a < Y.length; a++) {
      const w = c[a]!;
      if (w === 0) continue;
      const y = Y[a]!;
      for (let i = 0; i < f; i++) x[i] = x[i]! + w * y[i]!;
    }
    return x;
  });
}

// ───────────────────────── helpers ─────────────────────────

function dot(a: ArrayLike<number>, b: ArrayLike<number>): number {
  let s = 0;
  for (let i = 0; i < a.length; i++) s += a[i]! * b[i]!;
  return s;
}

function seeded(f: number, p: number): Float64Array[] {
  let state = 0x2f6b1a3d;
  const next = (): number => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return ((state >>> 0) / 0xffffffff) * 2 - 1;
  };
  return Array.from({ length: p }, () => Float64Array.from({ length: f }, next));
}

function mOrthonormalise(X: Float64Array[], M: ArrayLike<number>): void {
  for (let a = 0; a < X.length; a++) {
    const x = X[a]!;
    for (let b = 0; b < a; b++) {
      const y = X[b]!;
      let proj = 0;
      for (let i = 0; i < x.length; i++) proj += x[i]! * M[i]! * y[i]!;
      for (let i = 0; i < x.length; i++) x[i] = x[i]! - proj * y[i]!;
    }
    normaliseM(x, M);
  }
}

function normaliseM(x: Float64Array, M: ArrayLike<number>): void {
  let s = 0;
  for (let i = 0; i < x.length; i++) s += M[i]! * x[i]! * x[i]!;
  const k = s > 0 ? 1 / Math.sqrt(s) : 0;
  for (let i = 0; i < x.length; i++) x[i] = x[i]! * (k);
}

function triangleArea(P: ArrayLike<number>, a: number, b: number, c: number): number {
  const ux = P[b * 3]! - P[a * 3]!, uy = P[b * 3 + 1]! - P[a * 3 + 1]!, uz = P[b * 3 + 2]! - P[a * 3 + 2]!;
  const vx = P[c * 3]! - P[a * 3]!, vy = P[c * 3 + 1]! - P[a * 3 + 1]!, vz = P[c * 3 + 2]! - P[a * 3 + 2]!;
  const cx = uy * vz - uz * vy, cy = uz * vx - ux * vz, cz = ux * vy - uy * vx;
  return Math.sqrt(cx * cx + cy * cy + cz * cz) / 2;
}

/** cot of the angle at `o` in triangle (o, a, b). */
function cotAt(P: ArrayLike<number>, o: number, a: number, b: number): number {
  const ux = P[a * 3]! - P[o * 3]!, uy = P[a * 3 + 1]! - P[o * 3 + 1]!, uz = P[a * 3 + 2]! - P[o * 3 + 2]!;
  const vx = P[b * 3]! - P[o * 3]!, vy = P[b * 3 + 1]! - P[o * 3 + 1]!, vz = P[b * 3 + 2]! - P[o * 3 + 2]!;
  const d = ux * vx + uy * vy + uz * vz;
  const cx = uy * vz - uz * vy, cy = uz * vx - ux * vz, cz = ux * vy - uy * vx;
  const cr = Math.sqrt(cx * cx + cy * cy + cz * cz);
  return cr > 1e-300 ? d / cr : 0;
}

/** ∇φ per vertex: each face's constant gradient of the linear interpolant, area-averaged onto its corners. */
export function surfaceGradient(P: ArrayLike<number>, I: ArrayLike<number>, n: number, phi: ArrayLike<number>): Float64Array {
  const g = new Float64Array(3 * n);
  const w = new Float64Array(n);
  for (let t = 0; t + 2 < I.length; t += 3) {
    const a = I[t]!, b = I[t + 1]!, c = I[t + 2]!;
    const e1 = [P[b * 3]! - P[a * 3]!, P[b * 3 + 1]! - P[a * 3 + 1]!, P[b * 3 + 2]! - P[a * 3 + 2]!];
    const e2 = [P[c * 3]! - P[a * 3]!, P[c * 3 + 1]! - P[a * 3 + 1]!, P[c * 3 + 2]! - P[a * 3 + 2]!];
    const nx = e1[1]! * e2[2]! - e1[2]! * e2[1]!, ny = e1[2]! * e2[0]! - e1[0]! * e2[2]!, nz = e1[0]! * e2[1]! - e1[1]! * e2[0]!;
    const twoA = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (!(twoA > 0)) continue;
    // ∇φ = Σ φ_i (N × e_i) / 2A, e_i the edge opposite vertex i, N the unit normal.
    const N = [nx / twoA, ny / twoA, nz / twoA];
    const grad = [0, 0, 0];
    const corners: Array<[number, number, number]> = [[a, b, c], [b, c, a], [c, a, b]];
    for (const [i, j, k] of corners) {
      const ex = P[k * 3]! - P[j * 3]!, ey = P[k * 3 + 1]! - P[j * 3 + 1]!, ez = P[k * 3 + 2]! - P[j * 3 + 2]!;
      const cx = N[1]! * ez - N[2]! * ey, cy = N[2]! * ex - N[0]! * ez, cz = N[0]! * ey - N[1]! * ex;
      grad[0] = grad[0]! + (phi[i]! * cx) / twoA;
      grad[1] = grad[1]! + (phi[i]! * cy) / twoA;
      grad[2] = grad[2]! + (phi[i]! * cz) / twoA;
    }
    const area = twoA / 2;
    for (const v of [a, b, c]) {
      g[v * 3] = g[v * 3]! + area * grad[0]!;
      g[v * 3 + 1] = g[v * 3 + 1]! + area * grad[1]!;
      g[v * 3 + 2] = g[v * 3 + 2]! + area * grad[2]!;
      w[v] = w[v]! + area;
    }
  }
  for (let v = 0; v < n; v++) if (w[v]! > 0) for (let c = 0; c < 3; c++) g[v * 3 + c] = g[v * 3 + c]! / (w[v]!);
  return g;
}
